import cloneDeep from 'lodash/cloneDeep';
import type {
    IconAsset,
    Scene,
    SceneCategory,
    TextAsset,
    TimeAwareMediaAsset,
} from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';
import type { ChartAsset } from '@lumen5/luminary/core/asset/ChartAsset';

import { theseAreOverlapping } from 'js/src/data/transcript';
import { AssetSuffix } from 'js/src/editor/constants';
import { isNotNull } from 'js/src/libs/consts';
import {
    type BaseSceneSwapBridge,
    DEFAULT_COPY_STICKERS,
} from 'js/src/libs/scene-swapping/BaseBridge';
import { CommonToOrdinaryBridge } from 'js/src/libs/scene-swapping/CommonToOrdinaryBridge';
import { CommonToTalkingHeadBridge } from 'js/src/libs/scene-swapping/CommonToTalkingHeadBridge';
import { CommonToTalkingHeadInsertBridge } from 'js/src/libs/scene-swapping/CommonToTalkingHeadInsertBridge';
import { CommonToVoiceoverBridge } from 'js/src/libs/scene-swapping/CommonToVoiceoverBridge';
import { OrdinaryToCommonBridge } from 'js/src/libs/scene-swapping/OrdinaryToCommonBridge';
import { OrdinaryToTalkingHeadBridge } from 'js/src/libs/scene-swapping/OrdinaryToTalkingHeadBridge';
import { TalkingHeadInsertToCommonBridge } from 'js/src/libs/scene-swapping/TalkingHeadInsertToCommonBridge';
import { TalkingHeadInsertToVoiceoverBridge } from 'js/src/libs/scene-swapping/TalkingHeadInsertToVoiceoverBridge';
import { VoiceoverToCommonBridge } from 'js/src/libs/scene-swapping/VoiceoverToCommonBridge';
import { VoiceoverToTalkingHeadInsertBridge } from 'js/src/libs/scene-swapping/VoiceoverToTalkingHeadInsertBridge';
import {
    getEditableAssetsOnSceneLevel,
    getEditableAssetsOnSubsceneLevel,
    getVoiceDrivenAsset,
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    isAIAvatarSceneGroup,
    isMediaAssetEmpty,
    isTalkingHeadSceneGroup,
    isVoiceDrivenSceneGroup,
    isVoiceoverSceneGroup,
    makeVoicedrivenCompatibleScene,
    sceneContainsEditableMedia,
    standardizeTalkingHeadMediaAssetProperties,
    translateVideoTimeToTranscriptTime,
} from 'js/src/luminary';
import { getIconType, IconType } from 'js/src/luminary/icon-selectors';
import { getAssetCenterXY, isTextSticker } from 'js/src/luminary/luminary-utils';
import {
    getVoicedrivenSceneDuration,
    getVoicedrivenSceneEndTime,
    getVoicedrivenSceneStartTime,
    setVoicedrivenSceneDuration,
} from 'js/src/luminary/voicedriven-scenes';

type SwappableAsset = TimeAwareMediaAsset | TextAsset | IconAsset | ChartAsset;
type AssetContent = Record<string, any>;
export type AssetPair = {
    sourceAssetId: string;
    targetAssetId: string;
};
type AssetData = {
    assetContent: AssetContent;
    used: boolean;
    assetId: string;
    sticker?: boolean;
};
type TargetAsset = {
    asset: SwappableAsset;
    writtenTo: boolean;
};

const ASSET_NAME_REGEX = /(.*)_(\d)(_.*)?/;

function _getNameParts(name: string) {
    const match = name.match(ASSET_NAME_REGEX);
    if (!match) {
        return {
            parsedName: name,
            index: 0,
            prefix: name,
            suffix: '',
        };
    }
    return {
        parsedName: match[1] + (match[3] ?? ''), // remove index from name (e.g. "media_1" becomes "media" and "media_1_watermark" becomes "media__watermark")
        prefix: match[1],
        suffix: match[3] ?? '',
        index: parseInt(match[2]),
    };
}

function pushIfDefined<T>(array: T[], item: T | undefined) {
    if (item) {
        array.push(item);
    }
}

/**
 * True if the text asset's text value is empty or just whitespace
 */
function isTextAssetEmpty(textAsset: TextAsset): boolean {
    return textAsset.text.replace(/\s+/g, ' ').trim().length === 0;
}

function filterAssetsToCopy(assets: (TimeAwareMediaAsset | TextAsset | IconAsset | ChartAsset)[]) {
    return (
        assets
            // filter out non-editable assets
            .filter((asset) => asset.isEditable)
            // filter out empty media assets and talking head media assets
            .filter((asset) => {
                if (VideoData.isMediaAsset(asset)) {
                    const mediaAsset = asset;

                    if (isMediaAssetEmpty(mediaAsset)) {
                        return false;
                    }
                    if (mediaAsset.linkToSceneGroup) {
                        return false;
                    }
                }
                return true;
            })
            .filter((asset) => {
                const nameParts = _getNameParts(asset.name);
                return !nameParts.suffix.includes(AssetSuffix.DisableSwap);
            })
            // filter out waveform icons
            .filter(
                (asset) =>
                    !VideoData.isIconAsset(asset) || getIconType(asset) !== IconType.WAVEFORM,
            )
            // filter out empty text assets
            .filter((asset) => !VideoData.isTextAsset(asset) || !isTextAssetEmpty(asset))
    );
}

function cleanupProperties(assetContent: AssetContent) {
    // remove textSize key if present (we want to use target design's textSize!)
    delete assetContent.textSize;

    // remove certain properties which we want to preserve when switching aspect ratios but not swapping scenes:
    // text properties
    delete assetContent.normalStyle;
    delete assetContent.highlightStyle;
    delete assetContent.colorGraphics;
    delete assetContent.alphaGraphics;
    // color asset properties
    delete assetContent.sourceColor;
    delete assetContent.sourceAlpha;

    return assetContent;
}

/**
 * This is the SceneSwapper, which allows us to take the content (media files, text, icons) that the user
 * has applied to a sourceScene, and copy that content over to a targetScene (which is usually a different
 * scene design and therefore has different types/numbers of assets).
 *
 * The SceneSwapper uses an algorithm to determine which source pieces of content should be slotted into
 * which target assets.
 *
 * Content can only be copied over if the source and target assets have the same prefix & suffix in their names. They
 * are ordered by the "index" in the name.
 *
 * For example, if we have a source scene with the following scene-level assets: media_2, media_1_watermark, media_3
 * And a target scene with the following scene-level assets: media_1, media_2, media_3
 *
 * The SceneSwapper will copy over the following content:
 * - media_2 -> media_1 // the "first" media asset in the source scene is matched with the "first" media asset in the target scene
 * - media_3 -> media_2 // the "second" media asset in the source scene is matched with the "second" media asset in the target scene
 *
 * Note how the media_1_watermark is not copied over, because there is no matching asset in the target scene with the `_watermark` suffix.
 * Also note how media_2 is the "first" media asset in the source scene because there is no asset named "media_1".
 *
 * Using that asset-matching, the algorithm is as follows:
 *
 * - match source scene level assets with target scene level assets, as many as possible
 *     - record a list of all "unconsumed" source scene level assets
 * - match all source subscene level assets with target subscene level assets, as many as possible
 *     - record a list of all "unconsumed" source subscene level assets
 * - use the "unconsumed" source scene level assets to fill in any remaining placeholder content on the subscene level,
 *   for as many subscenes as you can fill
 * - use the "unconsumed" source subscene level assets to fill any remaining placeholder content on the scene level
 *
 * To see more of the thought behind this approach, see: https://www.notion.so/lumen5/Scene-swapping-overlays-35018f8d78164e13b5b86f9021539ff1
 */
export default class SceneSwapper {
    sourceScene: Scene;
    // assets
    sceneAssets: AssetData[] = [];
    subsceneAssets: AssetData[][] = [];
    // scene properties
    attribution: string | null;
    musicVolumeOverride: number | null;
    // duration properties
    duration: number;
    subsceneDurations: number[] = [];
    sourceSceneCategory: SceneCategory;
    // private
    #sceneCategoryBridges: BaseSceneSwapBridge[];

    constructor(sourceScene: Scene, sourceSentenceToUseInstead: string | undefined = undefined) {
        let originalAssetTexts: [string, string][] = [];
        if (sourceSentenceToUseInstead) {
            // update the scene so only a single editable text asset is set (to the source sentence)
            const editableTextAssets = sourceScene
                .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
                .filter((asset) => asset.isEditable);
            // not sure what we can do if there are no editable text assets!
            if (editableTextAssets.length > 0) {
                // store the original text for each editable text asset to restore later
                originalAssetTexts = editableTextAssets.map((asset) => [asset.id, asset.text]);
                // get the asset with the most text on the scene/first subscene
                const assetWithMostText = editableTextAssets.reduce((prev, curr) => {
                    if (curr.subscene !== null && curr.subscene.index > 0) {
                        return prev;
                    }
                    return prev.text.length > curr.text.length ? prev : curr;
                });
                // set text for all editable text assets
                editableTextAssets.forEach((asset) => {
                    asset.text = asset === assetWithMostText ? sourceSentenceToUseInstead : '';
                });
            }
        }

        this.sourceSceneCategory = sourceScene.sceneCategory;
        this.sourceScene = sourceScene;
        this.sceneAssets = this._getUserEditedSlideLevelElements(sourceScene);
        this.subsceneAssets = this._getUserEditedSubslideLevelElements(sourceScene);
        this.attribution = sourceScene.attribution;
        this.musicVolumeOverride = sourceScene.sceneGroup.musicVolumeOverride;
        this.duration = getVoicedrivenSceneDuration(sourceScene);
        this.subsceneDurations = sourceScene.subscenes.map(
            (subscene) => subscene.duration ?? getVoicedrivenSceneDuration(sourceScene),
        );

        /**
         * Ordinarily, the SceneSwapper prevents swapping from a scene design in one SceneCategory to a scene design
         * in another SceneCategory. For example, we don't want to allow users to swap from an Outro scene to a
         * Talking Head scene - what would that even mean?!
         *
         * However, there are some special cases where we do want to allow swapping from one SceneCategory to another.
         *
         * For those special cases, we have a "bridge" that connects those two SceneCategories. The bridges provide
         * specific rules about when swapping is allowed and what specific, special transformations need to be applied
         * to complete the swap.
         *
         * Bridges are 1-way, meaning that they can provide the ability to swap from SceneCategory A to SceneCategory B,
         * without providing the ability to swap from SceneCategory B to SceneCategory A.
         *
         * Details on the specific bridges can be found within the bridge files themselves.
         */
        this.#sceneCategoryBridges = [
            new OrdinaryToTalkingHeadBridge(),
            new OrdinaryToCommonBridge(),
            new CommonToTalkingHeadBridge(),
            new CommonToOrdinaryBridge(),
            new CommonToTalkingHeadInsertBridge(),
            new CommonToVoiceoverBridge(),
            new TalkingHeadInsertToCommonBridge(),
            new TalkingHeadInsertToVoiceoverBridge(),
            new VoiceoverToTalkingHeadInsertBridge(),
            new VoiceoverToCommonBridge(),
        ];

        // restore the original text for each editable text asset
        if (originalAssetTexts.length > 0) {
            originalAssetTexts.forEach(([assetId, text]) => {
                const asset = sourceScene.getAssetById(assetId) as TextAsset | undefined;
                if (asset) {
                    asset.text = text;
                }
            });
        }
    }

    canSwapToCategory(targetSceneCategory: SceneCategory): boolean {
        // you can swap if there's no change in scene category,
        // or if there's an explicit bridge between the scene categories
        return (
            this.sourceScene.sceneCategory === targetSceneCategory ||
            this.#sceneCategoryBridges.some((bridge) => {
                return bridge.isSwapAllowed(this.sourceScene, targetSceneCategory);
            })
        );
    }

    swapWillAlterSceneGroup(targetSceneCategory: SceneCategory): boolean {
        if (this.sourceSceneCategory === targetSceneCategory) {
            return false;
        } else {
            const bridge = this._getBridge(targetSceneCategory);
            if (!bridge) {
                return false;
            }
            return bridge.swapWillAlterSceneGroup;
        }
    }

    _getBridge(targetSceneCategory: SceneCategory) {
        return this.#sceneCategoryBridges.find((bridge) =>
            bridge.isSwapAllowed(this.sourceScene, targetSceneCategory),
        );
    }

    /**
     * Copy over content from the source scene to the target scene. This copies over all assets
     * and their contents and some limited user-editable properties.
     */
    copyContentToScene(
        targetScene: Scene,
        {
            maxSubscenesToCopy = Number.MAX_SAFE_INTEGER,
            copyVoicedrivenContent = true,
        }: {
            maxSubscenesToCopy?: number;
            copyVoicedrivenContent?: boolean;
        },
    ): AssetPair[] {
        const assetPairs: AssetPair[] = [];
        const bridge = this._getBridge(targetScene.sceneCategory);
        if (bridge) {
            bridge.doPreSwapTransformation(this.sourceScene, targetScene);
        }

        // set scene properties
        if (sceneContainsEditableMedia(targetScene)) {
            targetScene.attribution = this.attribution;
        }

        // set scene group properties
        const sceneGroup = targetScene.sceneGroup;
        sceneGroup.musicVolumeOverride = this.musicVolumeOverride;

        // copy over subscene-level content to subscene-level assets
        const targetSubsceneAssets = this._copySubscenes(
            targetScene,
            maxSubscenesToCopy,
            assetPairs,
        );

        // copy over scene-level content to scene-level assets
        const targetSceneAssets = this._createTargetAssetList(
            this._orderAssetsByName(
                this._filterAssetsToTarget(getEditableAssetsOnSceneLevel(targetScene)),
            ),
        );

        // copy over assets that are not part of the scene design
        const copyStickers = bridge?._copyStickers ?? DEFAULT_COPY_STICKERS;
        if (copyStickers) {
            this.sceneAssets
                .filter((assetData) => !assetData.used && assetData.sticker)
                .forEach((assetData) => {
                    if (!assetData.assetContent.classType) {
                        assetData.used = true;
                        return;
                    }
                    const asset = targetScene.addSticker(assetData.assetContent.classType);
                    const targetAsset: TargetAsset = {
                        asset,
                        writtenTo: false,
                    };
                    pushIfDefined(assetPairs, this._copyAssetDataToAsset(targetAsset, assetData));
                });
        }

        this.sceneAssets.forEach((assetData) => {
            const asset = this._findNextUnusedTargetAsset(
                targetSceneAssets,
                assetData.assetContent.name,
            );
            pushIfDefined(assetPairs, this._copyAssetDataToAsset(asset, assetData));
        });

        // for any left-over scene-level content, add it to the subscene-level assets
        this.sceneAssets
            .filter((assetData) => !assetData.used)
            .forEach((assetData) => {
                const asset = this._findNextUnusedTargetAssetInNestedList(
                    targetSubsceneAssets,
                    assetData.assetContent.name,
                );
                pushIfDefined(assetPairs, this._copyAssetDataToAsset(asset, assetData));
            });

        // and finally, for any left-over subscene-level content, add it to the scene-level assets
        this.subsceneAssets.forEach((subsceneAssetDatas) => {
            subsceneAssetDatas
                .filter((assetData) => !assetData.used)
                .forEach((assetData) => {
                    const asset = this._findNextUnusedTargetAsset(
                        targetSceneAssets,
                        assetData.assetContent.name,
                    );
                    pushIfDefined(assetPairs, this._copyAssetDataToAsset(asset, assetData));
                });
        });

        if (copyVoicedrivenContent) {
            this._copyVoicedrivenContent(targetScene);
        }

        if (isVoiceDrivenSceneGroup(this.sourceScene.sceneGroup)) {
            makeVoicedrivenCompatibleScene(targetScene);
        }

        // resize text to fit any new text content
        targetScene
            .getAssetsByType(VideoData.TEXT_ASSET_TYPE)
            .filter((text) => text.isEditable)
            .forEach((textAsset) => {
                textAsset.setFontSizeToFitAllText();
            });

        if (bridge) {
            bridge.doPostSwapTransformation(this.sourceScene, targetScene);
        }

        this._reset();

        return assetPairs;
    }

    _copyVoicedrivenContent(targetScene: Scene) {
        if (isTalkingHeadSceneGroup(this.sourceScene.sceneGroup)) {
            if (!targetScene.sceneGroup.mediaAsset) {
                targetScene.sceneGroup.addMediaAsset();
            }
            const assetData = cleanupProperties(
                this.sourceScene.sceneGroup.mediaAsset.saveEditable(),
            );
            targetScene.sceneGroup.mediaAsset.loadEditable(assetData);

            standardizeTalkingHeadMediaAssetProperties(targetScene, this.sourceScene, false);
        }
        if (isVoiceoverSceneGroup(this.sourceScene.sceneGroup)) {
            if (!targetScene.sceneGroup.voiceoverAsset) {
                targetScene.sceneGroup.addVoiceoverAsset();
            }
            const sourceData = this.sourceScene.sceneGroup.voiceoverAsset.sourceData;
            if (sourceData === null) {
                throw new Error('sourceScene.sceneGroup.voiceoverAsset.sourceData is null');
            }
            targetScene.sceneGroup.voiceoverAsset.sourceData = sourceData;
        }
        if (isAIAvatarSceneGroup(this.sourceScene.sceneGroup)) {
            // foreground
            if (!targetScene.sceneGroup.avatar.foreground) {
                targetScene.sceneGroup.avatar.addForeground();
            }
            if (targetScene.sceneGroup.avatar.foreground) {
                if (this.sourceScene.sceneGroup.avatar.foreground === null)
                    throw new Error('sourceScene.sceneGroup.avatar.foreground is null');
                const assetData = cleanupProperties(
                    this.sourceScene.sceneGroup.avatar.foreground.saveEditable(),
                );
                targetScene.sceneGroup.avatar.foreground.loadEditable(assetData);
            }
            standardizeTalkingHeadMediaAssetProperties(targetScene, this.sourceScene, false);

            // background
            if (
                !targetScene.sceneGroup.avatar.background &&
                this.sourceScene.sceneGroup.avatar.background
            ) {
                targetScene.sceneGroup.avatar.addBackground();
                targetScene.sceneGroup.avatar.background!.load(
                    this.sourceScene.sceneGroup.avatar.background.save(),
                );
            }
        }

        const hasATranscript = !this.sourceScene.sceneGroup.originalTranscript.empty;
        if (!hasATranscript) return;

        // AI-Avatar-only sceneGroups and sceneGroups whose audio asset was removed
        // while the transcript stayed have a non-empty transcript but no asset to cut.
        const voicedrivenAsset = getVoiceDrivenAsset(targetScene.sceneGroup);
        if (!voicedrivenAsset) return;

        const sourceVoiceDrivenAssetPlaybackRegions = getVoiceDrivenAssetPlaybackRegions(
            this.sourceScene.sceneGroup,
        );
        const sourceVoiceDrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(
            this.sourceScene.sceneGroup,
        );

        const sourceStartTime = translateVideoTimeToTranscriptTime(
            getVoicedrivenSceneStartTime(this.sourceScene),
            sourceVoiceDrivenAssetPlaybackRegions,
            sourceVoiceDrivenAssetSourceDuration,
            this.sourceScene.sceneGroup.startTime,
        );
        const sourceEndTime = translateVideoTimeToTranscriptTime(
            getVoicedrivenSceneEndTime(this.sourceScene),
            sourceVoiceDrivenAssetPlaybackRegions,
            sourceVoiceDrivenAssetSourceDuration,
            this.sourceScene.sceneGroup.startTime,
            {
                includeTrailingCut: false,
            },
        );
        targetScene.sceneGroup.originalTranscript.transcriptData = cloneDeep(
            this.sourceScene.sceneGroup.originalTranscript.transcriptData,
        );

        const targetVoiceDrivenAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(
            targetScene.sceneGroup,
        );

        const playbackRegionsDuringScene = sourceVoiceDrivenAssetPlaybackRegions
            .map(([regionStart, regionEnd]): [number, number] | null => {
                // only select regions that overlap with the part of the transcript that we are creating an
                // overlay for, and don't exceed the duration of the target voice driven asset
                if (!theseAreOverlapping(regionStart, regionEnd, sourceStartTime, sourceEndTime)) {
                    return null;
                }
                return [
                    Math.max(regionStart, sourceStartTime),
                    Math.min(regionEnd, sourceEndTime, targetVoiceDrivenAssetSourceDuration),
                ];
            })
            .filter(isNotNull);

        const cutRegions = VideoData.getCutRegions(
            playbackRegionsDuringScene,
            targetVoiceDrivenAssetSourceDuration,
        );

        cutRegions.forEach((region) => {
            voicedrivenAsset.cut(region[0], region[1]);
        });
    }

    _copySubscenes(
        targetScene: Scene,
        maxSubscenesToCopy: number,
        assetPairs: AssetPair[],
    ): TargetAsset[][] {
        // ensure we have a matching number of subscenes
        while (
            targetScene.subscenes.length <
            Math.min(this.subsceneDurations.length, maxSubscenesToCopy)
        ) {
            targetScene.addSubscene();
        }

        if (maxSubscenesToCopy < this.subsceneDurations.length) {
            // we're not copying over all subscenes, so set the duration to just the duration of the subscenes we are copying
            setVoicedrivenSceneDuration(
                targetScene,
                this.subsceneDurations
                    .slice(0, maxSubscenesToCopy)
                    .reduce((curr, duration) => curr + duration, 0),
            );
        } else {
            // for voice-driven, we have an assumption of 1 subscene per scene, so setting the scene's duration and subscene's duration
            // should be interchangeable
            setVoicedrivenSceneDuration(targetScene, this.duration);
        }

        // copy over subscene assets (if a matching subscene asset exists!)
        return this.subsceneAssets.slice(0, maxSubscenesToCopy).map((subsceneAssetDatas, index) => {
            if (targetScene.subscenes.length <= index) {
                targetScene.addSubscene(index);
            }
            const targetSubSceneAssets = this._createTargetAssetList(
                this._orderAssetsByName(
                    this._filterAssetsToTarget(
                        getEditableAssetsOnSubsceneLevel(targetScene.subscenes[index]),
                    ),
                ),
            );
            subsceneAssetDatas.forEach((assetData) => {
                const asset = this._findNextUnusedTargetAsset(
                    targetSubSceneAssets,
                    assetData.assetContent.name,
                );
                pushIfDefined(assetPairs, this._copyAssetDataToAsset(asset, assetData));
            });
            return targetSubSceneAssets;
        });
    }

    _findNextUnusedTargetAsset(
        targetAssets: TargetAsset[],
        assetName: string,
    ): TargetAsset | undefined {
        // we want to find the next unused target asset with the same parsedName as the source asset
        const sourceNameParts = _getNameParts(assetName);
        // Priotize matching asset names
        const matchingTargetAsset = targetAssets.find((targetAsset) => {
            return targetAsset.asset.name === assetName && targetAsset.writtenTo === false;
        });
        if (matchingTargetAsset) {
            return matchingTargetAsset;
        }
        // Otherwise find first available
        return targetAssets.find((targetAsset) => {
            if (targetAsset.writtenTo) {
                return false;
            }

            const targetAssetName = targetAsset.asset.name;
            const targetNameParts = _getNameParts(targetAssetName);

            return sourceNameParts.parsedName === targetNameParts.parsedName;
        });
    }

    _findNextUnusedTargetAssetInNestedList(
        targetAssets: TargetAsset[][],
        assetName: string,
    ): TargetAsset | undefined {
        for (let i = 0; i < targetAssets.length; i++) {
            const targetAsset = this._findNextUnusedTargetAsset(targetAssets[i], assetName);
            if (targetAsset) {
                return targetAsset;
            }
        }
    }

    _reset() {
        this.sceneAssets.forEach((assetData) => {
            assetData.used = false;
        });
        this.subsceneAssets.forEach((subsceneAssetDatas) => {
            subsceneAssetDatas.forEach((assetData) => {
                assetData.used = false;
            });
        });
    }

    _copyAssetDataToAsset(
        targetAsset: TargetAsset | undefined,
        assetData: AssetData,
    ): AssetPair | undefined {
        if (!targetAsset || !targetAsset.asset) {
            return;
        }
        if (!targetAsset.asset?.isEditable) {
            return;
        }

        if (assetData.sticker) {
            // load all properties
            targetAsset.asset.load(assetData.assetContent);
        } else {
            if (VideoData.isIconAsset(targetAsset.asset)) {
                // for icons don't use loadEditable(), use replace() instead to ensure colours don't copy over
                targetAsset.asset.replace(assetData.assetContent.sourceData);
            } else {
                targetAsset.asset.loadEditable(assetData.assetContent);
            }
        }

        targetAsset.writtenTo = true;
        assetData.used = true;
        return {
            sourceAssetId: assetData.assetId,
            targetAssetId: targetAsset.asset.id,
        };
    }

    /**
     * Retrieve a list of non-empty ("edited") slide level
     * editable elements from the slide object
     */
    _getUserEditedSlideLevelElements(scene: Scene): AssetData[] {
        const assets = this._orderAssetsByName(
            filterAssetsToCopy(getEditableAssetsOnSceneLevel(scene)),
        );
        const result: AssetData[] = [];
        const textStickerEntries: AssetData[] = [];
        assets.forEach((asset) => {
            const sticker = VideoData.isStickerAsset(asset);
            if (sticker) {
                if (isTextSticker(asset)) {
                    const textAssets = VideoData.isAssetGroup(asset)
                        ? asset.getAssets().filter(VideoData.isTextAsset)
                        : [asset as TextAsset];
                    textAssets
                        .filter((textAsset) => !isTextAssetEmpty(textAsset))
                        .forEach((textAsset) => {
                            const assetContent = cleanupProperties(textAsset.saveEditable());
                            assetContent.name = `text_0`;
                            textStickerEntries.push({
                                assetId: textAsset.id,
                                assetContent,
                                used: false,
                                sticker: false,
                            });
                        });
                    return;
                }
                const assetContent = asset.save();
                if (assetContent.parentId) {
                    assetContent.transformation.anchor.x = 0.5;
                    assetContent.transformation.anchor.y = 0.5;
                    const { x, y } = getAssetCenterXY(asset);
                    assetContent.transformation.position.x = x;
                    assetContent.transformation.position.y = y;
                }
                result.push({
                    assetId: asset.id,
                    assetContent: cleanupProperties(assetContent),
                    used: false,
                    sticker: true,
                });
                return;
            }
            const assetContent = cleanupProperties(asset.saveEditable());
            result.push({
                assetId: asset.id,
                assetContent,
                used: false,
                sticker: false,
            });
        });
        return [...result, ...textStickerEntries];
    }

    /**
     * Retrieve a list of non-empty ("edited") subscene-level
     * editable elements from the slide object
     */
    _getUserEditedSubslideLevelElements(scene: Scene): AssetData[][] {
        if (scene.subscenes.length === 0) {
            return [];
        }
        return scene.subscenes.map((subscene) => {
            const assets = this._orderAssetsByName(
                filterAssetsToCopy(getEditableAssetsOnSubsceneLevel(subscene)),
            );
            return assets.map((asset) => {
                return {
                    assetId: asset.id,
                    assetContent: cleanupProperties(asset.saveEditable()),
                    used: false,
                };
            });
        });
    }

    _createTargetAssetList<T extends SwappableAsset>(assets: T[]): TargetAsset[] {
        return assets.map((asset) => {
            return {
                asset,
                writtenTo: false,
            };
        });
    }

    _orderAssetsByName<T extends SwappableAsset>(assets: T[]): T[] {
        return assets.sort((a, b) => {
            const aParts = _getNameParts(a.name);
            const bParts = _getNameParts(b.name);

            if (aParts.parsedName === bParts.parsedName) {
                return aParts.index - bParts.index;
            }

            return aParts.parsedName < bParts.parsedName ? -1 : 1;
        });
    }

    _filterAssetsToTarget<T extends SwappableAsset>(assets: T[]): T[] {
        return assets.filter((asset) => {
            if (VideoData.isMediaAsset(asset)) {
                if (asset.linkToSceneGroup) {
                    return false;
                }
            }
            // filter out waveform icons
            if (VideoData.isIconAsset(asset) && getIconType(asset) === IconType.WAVEFORM) {
                return false;
            }
            return true;
        });
    }
}
